import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { colors, s, ui, chartBase } from '../styles/theme'
import { useIsMobile } from '../hooks/useViewport'
import Chart from '../components/Chart'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const TUR_ADI = {
  'seo-gunluk': 'Günlük SEO taraması',
  'seo-aylik': 'Aylık SEO taraması',
  geo: 'GEO taraması',
}

const TUR_KISA = {
  'seo-gunluk': 'Günlük SEO',
  'seo-aylik': 'Aylık SEO',
  geo: 'GEO',
}

const TUR_KAYNAK = {
  'seo-gunluk': 'Yalnız veritabanı kontrolleri, siteye istek atılmaz',
  'seo-aylik': 'Veritabanı kontrolleri ve sayfa şablonu örneklemi',
  geo: 'Bizim içerik eklediğimiz ürünlerin sayfaları',
}

const AGIRLIK_ADI = { kritik: 'Kritik', yuksek: 'Yüksek', orta: 'Orta', dusuk: 'Düşük' }
const AGIRLIK_SIRA = { kritik: 0, yuksek: 1, orta: 2, dusuk: 3 }
const KATEGORI_ADI = {
  kunye: 'Künye alanı',
  icerik: 'İçerik',
  teknik: 'Sayfa şablonu',
  geo: 'GEO yayını',
}

function sayi(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function bin(v) {
  return Number(v || 0).toLocaleString('tr-TR')
}

function ondalik(v, basamak = 1) {
  const n = sayi(v)
  if (n === null) return '-'
  return n.toLocaleString('tr-TR', { minimumFractionDigits: basamak, maximumFractionDigits: basamak })
}

function oranYuzde(oran) {
  const n = sayi(oran)
  if (n === null) return '-'
  const y = n * 100
  return ondalik(y, y >= 10 ? 0 : 1) + '%'
}

function tarih(t) {
  if (!t) return '-'
  return new Date(t).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function sure(bas, bit) {
  if (!bas || !bit) return null
  const sn = Math.round((new Date(bit) - new Date(bas)) / 1000)
  if (sn < 0) return null
  if (sn < 60) return sn + ' sn'
  return Math.floor(sn / 60) + ' dk ' + (sn % 60) + ' sn'
}

function puanRengi(puan) {
  const p = sayi(puan)
  if (p === null) return colors.textMuted
  if (p >= 90) return colors.success
  if (p >= 70) return colors.warning
  return colors.danger
}

function puanEtiketi(puan) {
  const p = sayi(puan)
  if (p === null) return 'Ölçülmedi'
  if (p >= 90) return 'İyi'
  if (p >= 70) return 'Orta'
  return 'Zayıf'
}

function yorumNotu(kosum) {
  const sebep = (kosum.ozet && kosum.ozet.yorumAtlandi) || ''
  if (sebep === 'gunluk yapay zeka siniri doldu') {
    return 'Bu taramada yapay zeka yorumu üretilmedi, günlük yapay zeka sınırı dolmuştu. Ölçüm ve bulgular bundan etkilenmez.'
  }
  if (sebep) {
    return 'Bu taramada yapay zeka yorumu üretilmedi. Ölçüm ve bulgular bundan etkilenmez.'
  }
  return 'Bu taramada yapay zeka yorumu üretilmedi. Yorum aylık SEO taramasında, günlük GEO taramasında ve elle başlatılan taramalarda üretilir.'
}

function agirlikKutusu(agirlik) {
  if (agirlik === 'kritik') return { ...s.badge, background: colors.dangerLight, color: colors.dangerText }
  if (agirlik === 'yuksek') return { ...s.badge, background: colors.warningLight, color: colors.warningText }
  if (agirlik === 'orta') return { ...s.badge, background: colors.infoLight, color: colors.infoText }
  return s.badgeGray
}

function sablonCapinda(b) {
  return b.kategori === 'teknik' && sayi(b.oran) !== null && sayi(b.oran) >= 0.95
}

function PuanHalkasi({ puan, boyut = 96 }) {
  const p = sayi(puan)
  const kalinlik = 8
  const r = (boyut - kalinlik) / 2
  const cevre = 2 * Math.PI * r
  const dolu = p === null ? 0 : Math.max(0, Math.min(100, p)) / 100
  const renk = puanRengi(p)
  return (
    <div style={{ position: 'relative', width: boyut, height: boyut, flexShrink: 0 }}>
      <svg width={boyut} height={boyut} viewBox={'0 0 ' + boyut + ' ' + boyut} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={boyut / 2} cy={boyut / 2} r={r} fill="none" stroke={colors.borderLight} strokeWidth={kalinlik} />
        {dolu > 0 && (
          <circle cx={boyut / 2} cy={boyut / 2} r={r} fill="none" stroke={renk} strokeWidth={kalinlik} strokeLinecap="round"
            strokeDasharray={(cevre * dolu) + ' ' + cevre} />
        )}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px' }}>
        <span style={{ fontSize: '21px', fontWeight: 700, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', color: colors.textPrimary, lineHeight: 1 }}>
          {p === null ? '-' : ondalik(p, 1)}
        </span>
        <span style={{ fontSize: '10.5px', color: colors.textMuted }}>{puanEtiketi(p)}</span>
      </div>
    </div>
  )
}

function Olcu({ etiket, deger, alt }) {
  return (
    <div style={ui.statBox}>
      <p style={ui.statLabel}>{etiket}</p>
      <p style={ui.statValue}>{deger}</p>
      {alt && <p style={{ fontSize: '11.5px', color: colors.textMuted, margin: '4px 0 0' }}>{alt}</p>}
    </div>
  )
}

function OrnekListesi({ ornekler, sinir }) {
  const liste = (ornekler || []).slice(0, sinir || 12)
  if (!liste.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px' }}>
      {liste.map((o, i) => (
        <div key={(o.id || '') + '-' + i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', fontSize: '12.5px', minWidth: 0 }}>
          <span style={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '11.5px', flexShrink: 0 }}>
            {o.id || '—'}
          </span>
          {o.url
            ? <a href={o.url} target="_blank" rel="noreferrer" style={{ color: colors.primary, textDecoration: 'none', wordBreak: 'break-word', minWidth: 0 }}>{o.ad}</a>
            : <span style={{ color: colors.textPrimary, wordBreak: 'break-word', minWidth: 0 }}>{o.ad}</span>}
          {o.adet > 1 && <span style={ui.chip}>{o.adet} ürün aynı</span>}
          {o.not && <span style={ui.chip}>{o.not}</span>}
        </div>
      ))}
    </div>
  )
}

function TamListe({ bulgu, markaId, api, headers }) {
  const SAYFA = 200
  const [acik, setAcik] = useState(false)
  const [satirlar, setSatirlar] = useState([])
  const [toplam, setToplam] = useState(0)
  const [kaynak, setKaynak] = useState('')
  const [taramaAnindaki, setTaramaAnindaki] = useState(0)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [indiriliyor, setIndiriliyor] = useState(false)
  const [hata, setHata] = useState('')

  const taban = api + '/admin/brands/' + markaId + '/sgo/bulgu/' + bulgu.id

  function yukle(offset) {
    setYukleniyor(true)
    setHata('')
    axios.get(taban + '/liste', { params: { limit: SAYFA, offset: offset }, headers: headers })
      .then(r => {
        const d = r.data || {}
        setToplam(Number(d.toplam || 0))
        setKaynak(String(d.kaynak || ''))
        setTaramaAnindaki(Number(d.taramaAnindaki || 0))
        if (offset) setSatirlar(s => s.concat(d.satirlar || []))
        else setSatirlar(d.satirlar || [])
        setAcik(true)
      })
      .catch(e => setHata((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setYukleniyor(false))
  }

  function csvIndir() {
    setIndiriliyor(true)
    setHata('')
    axios.get(taban + '/csv', { headers: headers, responseType: 'blob' })
      .then(r => {
        const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }))
        const a = document.createElement('a')
        a.href = url
        a.download = 'sgo-marka' + markaId + '-' + bulgu.kontrol + '.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      })
      .catch(() => setHata('Dosya indirilemedi, tekrar deneyin.'))
      .finally(() => setIndiriliyor(false))
  }

  return (
    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid ' + colors.borderLight }}>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        {acik
          ? <button style={ui.btnQuiet} onClick={() => { setAcik(false); setSatirlar([]) }}>Tam listeyi kapat</button>
          : <button style={ui.btnQuiet} onClick={() => yukle(0)} disabled={yukleniyor}>
              {yukleniyor ? 'Yükleniyor…' : 'Tam listeyi gör'}
            </button>}
        <button style={ui.btnQuiet} onClick={csvIndir} disabled={indiriliyor}>
          {indiriliyor ? 'Hazırlanıyor…' : 'CSV indir'}
        </button>
      </div>

      {hata && <p style={{ ...ui.emptyText, color: colors.danger, margin: '8px 0 0' }}>{hata}</p>}

      {acik && (
        <>
          <p style={{ fontSize: '11.5px', color: colors.textMuted, margin: '8px 0 6px', lineHeight: 1.6 }}>
            {kaynak === 'canli'
              ? 'Liste şu anki katalogdan üretildi, toplam ' + bin(toplam) + ' kayıt.' +
                (toplam !== taramaAnindaki
                  ? ' Tarama anında ' + bin(taramaAnindaki) + ' kayıt vardı, fark tarama sonrası yapılan değişikliklerden gelir.'
                  : '')
              : 'Liste tarama anında kaydedildi, toplam ' + bin(toplam) + ' kayıt.'}
          </p>
          {!satirlar.length
            ? <p style={ui.emptyText}>Listelenecek kayıt bulunamadı.</p>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Ürün kodu</th>
                      <th style={s.th}>Ad</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Aynı olan</th>
                      <th style={s.th}>Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satirlar.map((o, i) => (
                      <tr key={(o.id || '') + '-' + i}>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '11.5px', whiteSpace: 'nowrap' }}>{o.id || '—'}</td>
                        <td style={s.td}>
                          {o.url
                            ? <a href={o.url} target="_blank" rel="noreferrer" style={{ color: colors.primary, textDecoration: 'none', wordBreak: 'break-word' }}>{o.ad}</a>
                            : <span style={{ wordBreak: 'break-word' }}>{o.ad}</span>}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {o.adet > 1 ? bin(o.adet) : ''}
                        </td>
                        <td style={{ ...s.td, color: colors.textMuted, fontSize: '12px' }}>{o.not || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {satirlar.length < toplam && (
            <button style={{ ...ui.btnQuiet, marginTop: '8px' }} onClick={() => yukle(satirlar.length)} disabled={yukleniyor}>
              {yukleniyor ? 'Yükleniyor…' : 'Daha fazla yükle, ' + bin(toplam - satirlar.length) + ' kayıt kaldı'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

function SadeKusur({ bulgu, acikMi, ac, markaId, api, headers }) {
  const etkilenen = Number(bulgu.etkilenen || 0)
  const toplam = Number(bulgu.toplam || 0)
  return (
    <div style={{ ...s.cardSm, marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: colors.textPrimary }}>{bulgu.baslik}</span>
            <span style={agirlikKutusu(bulgu.agirlik)}>{AGIRLIK_ADI[bulgu.agirlik] || bulgu.agirlik}</span>
            {sablonCapinda(bulgu) && <span style={ui.chip}>Şablon çapında</span>}
          </div>
          <p style={{ fontSize: '12.5px', color: colors.textSecondary, margin: '6px 0 0', lineHeight: 1.6 }}>
            {bulgu.oneri || bulgu.aciklama}
          </p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: colors.textPrimary }}>
            {bin(etkilenen)}
          </div>
          <div style={{ fontSize: '11.5px', color: colors.textMuted }}>
            {toplam ? bin(toplam) + ' içinde · ' + oranYuzde(bulgu.oran) : 'adet'}
          </div>
        </div>
      </div>
      {(bulgu.ornekler || []).length > 0 && (
        <>
          <button style={{ ...ui.btnQuiet, padding: '4px 0', marginTop: '8px' }} onClick={ac}>
            {acikMi ? 'Örnekleri kapat' : 'Örnek ürünleri gör'}
          </button>
          {acikMi && (
            <>
              <OrnekListesi ornekler={bulgu.ornekler} sinir={12} />
              {etkilenen > Math.min(12, (bulgu.ornekler || []).length) && (
                <p style={{ fontSize: '11.5px', color: colors.textMuted, margin: '8px 0 0' }}>
                  İlk {bin(Math.min(12, (bulgu.ornekler || []).length))} tanesi gösteriliyor, toplam {bin(etkilenen)} kayıt var.
                </p>
              )}
              <TamListe bulgu={bulgu} markaId={markaId} api={api} headers={headers} />
            </>
          )}
        </>
      )}
    </div>
  )
}

function OzellikKutusu({ bulgular, esik }) {
  if (!bulgular.length) return null
  return (
    <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
      <p style={{ ...s.sectionTitle, marginBottom: '2px' }}>Katalogun genel durumu</p>
      <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0 0 12px', lineHeight: 1.6 }}>
        Aşağıdaki maddeler katalogun {oranYuzde(esik)} sınırından fazlasını kapsıyor. Tek tek düzeltilecek kusur değil,
        katalogun genel durumu sayılırlar ve puanı düşürmezler.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {bulgular.map(b => (
          <div key={b.id} style={{ display: 'flex', alignItems: 'baseline', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12.5px', color: colors.textPrimary, minWidth: 0 }}>{b.baslik}</span>
            <span style={{ fontSize: '12.5px', color: colors.textSecondary, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {bin(b.etkilenen)} ürün · {oranYuzde(b.oran)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Oncelikler({ liste }) {
  if (!liste || !liste.length) return null
  return (
    <div style={{ marginTop: '14px' }}>
      <p style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, margin: '0 0 8px' }}>Öncelik sırası</p>
      <ol style={{ margin: 0, padding: '0 0 0 20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {liste.map((o, i) => (
          <li key={i} style={{ fontSize: '13px', color: colors.textPrimary, lineHeight: 1.6 }}>{o}</li>
        ))}
      </ol>
    </div>
  )
}

function KelimeGrafigi({ kelime }) {
  const option = useMemo(() => {
    const bantlar = (kelime && kelime.bantlar) || []
    return {
      ...chartBase,
      tooltip: { ...chartBase.tooltip, formatter: p => p[0].name + '<br/>' + bin(p[0].value) + ' ürün' },
      xAxis: {
        type: 'category',
        data: bantlar.map(b => b.etiket + ' (' + b.bant + ')'),
        ...chartBase.xAxisStyle,
        axisLabel: { ...chartBase.xAxisStyle.axisLabel, interval: 0, fontSize: 10.5 },
      },
      yAxis: {
        type: 'value',
        ...chartBase.yAxisStyle,
        axisLabel: { ...chartBase.yAxisStyle.axisLabel, formatter: v => bin(v) },
      },
      series: [{
        type: 'bar',
        data: bantlar.map(b => b.adet),
        barMaxWidth: 46,
        itemStyle: { color: colors.primary, borderRadius: [4, 4, 0, 0] },
      }],
    }
  }, [kelime])

  if (!kelime || !(kelime.bantlar || []).length) return null

  return (
    <div style={s.card}>
      <div style={{ ...s.flexBetween, flexWrap: 'wrap', gap: '10px', marginBottom: '6px' }}>
        <div style={{ minWidth: 0 }}>
          <p style={s.sectionTitle}>Açıklama uzunluğu dağılımı</p>
          <p style={s.sectionSubtitle}>Ölçülüyor ve gösteriliyor, puana işlemiyor</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span style={ui.chip}>Ortalama {bin(kelime.ortalama)} kelime</span>
          <span style={ui.chip}>Medyan {bin(kelime.medyan)} kelime</span>
        </div>
      </div>
      <Chart option={option} height={230} />
    </div>
  )
}

function SahaOzeti({ ozet }) {
  if (!ozet) return null
  const satirlar = []
  if (ozet.sablon && ozet.sablon.hata) {
    satirlar.push(['Şablon örneklemi', 'Hata: ' + ozet.sablon.hata])
  } else if (ozet.sablon) {
    satirlar.push(['Şablon örneklemi', bin(ozet.sablon.basarili) + ' / ' + bin(ozet.sablon.denenen) + ' sayfa okundu'])
  }
  if (ozet.sayfasiAcilan != null) satirlar.push(['Sayfası açılan ürün', bin(ozet.sayfasiAcilan)])
  if (ozet.kayitsizIzli != null) satirlar.push(['İzi olup kaydı olmayan ürün', bin(ozet.kayitsizIzli)])
  if (ozet.robots) {
    satirlar.push([
      'Yapay zeka tarayıcısı',
      bin(ozet.robots.izinli) + ' izinli' + ((ozet.robots.engelli || []).length ? ' · ' + (ozet.robots.engelli || []).join(', ') + ' engelli' : ' · engel yok'),
    ])
  }
  if (ozet.llms) {
    satirlar.push(['llms.txt', ozet.llms.var ? bin(ozet.llms.boyut) + ' bayt' : 'yok'])
  }
  if (ozet.tavanaDayandi) satirlar.push(['GEO kapsamı', 'ürün tavanına dayanıldı, liste kesildi'])
  if (ozet.yalnizSiteDuzeyi) satirlar.push(['GEO kapsamı', 'GEO içeriği olan ürün yok, puan yalnız robots.txt ve llms.txt kontrollerinden'])
  if (ozet.yorumAtlandi) satirlar.push(['Yapay zeka yorumu', 'atlandı: ' + ozet.yorumAtlandi])
  else if (ozet.yorumJetonu) satirlar.push(['Yapay zeka yorumu', bin(ozet.yorumJetonu) + ' jeton harcandı'])
  if (!satirlar.length) return null

  return (
    <div style={s.card}>
      <p style={{ ...s.sectionTitle, marginBottom: '12px' }}>Saha ölçümü</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {satirlar.map(([a, b], i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap', borderBottom: i < satirlar.length - 1 ? '1px solid ' + colors.borderLight : 'none', paddingBottom: '8px' }}>
            <span style={{ fontSize: '12.5px', color: colors.textSecondary }}>{a}</span>
            <span style={{ fontSize: '12.5px', color: colors.textPrimary, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>{b}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SmartSgo() {
  const { selectedBrand, account } = useBrand()
  const mobil = useIsMobile()
  const token = localStorage.getItem('adminToken')
  const headers = useMemo(() => ({ Authorization: token }), [token])
  const teknikYetki = !!account
  const yonetimYetki = account && account.role === 'super_admin'

  const [ozet, setOzet] = useState(null)
  const [gecmis, setGecmis] = useState([])
  const [detay, setDetay] = useState({})
  const [sekme, setSekme] = useState('sade')
  const [seoSecim, setSeoSecim] = useState(null)
  const [teknikSecim, setTeknikSecim] = useState(null)
  const [acik, setAcik] = useState({})
  const [yukleniyor, setYukleniyor] = useState(true)
  const [busy, setBusy] = useState(false)
  const [sablonDahil, setSablonDahil] = useState(false)
  const [yorumUret, setYorumUret] = useState(true)
  const [msg, setMsg] = useState(null)
  const istenen = useRef({})
  const aktifMarka = useRef(null)

  const markaId = selectedBrand && selectedBrand.id

  function flash(text, ok = true) {
    setMsg({ text, ok })
    setTimeout(() => setMsg(null), 5000)
  }

  const ozetGetir = useCallback((sessiz) => {
    if (!markaId) return
    const bu = markaId
    if (!sessiz) setYukleniyor(true)
    Promise.all([
      axios.get(API + '/admin/brands/' + bu + '/sgo/ozet', { headers }),
      axios.get(API + '/admin/brands/' + bu + '/sgo/gecmis', { headers }),
    ])
      .then(([o, g]) => {
        if (aktifMarka.current !== bu) return
        setOzet(o.data)
        setGecmis((g.data && g.data.gecmis) || [])
      })
      .catch(e => {
        if (aktifMarka.current !== bu) return
        if (!sessiz) flash((e.response && e.response.data && e.response.data.error) || e.message, false)
      })
      .finally(() => { if (aktifMarka.current === bu) setYukleniyor(false) })
  }, [markaId, headers])

  const detayGetir = useCallback((runId) => {
    if (!markaId || !runId || istenen.current[runId]) return
    const bu = markaId
    istenen.current[runId] = true
    axios.get(API + '/admin/brands/' + bu + '/sgo/kosum/' + runId, { headers })
      .then(r => { if (aktifMarka.current === bu) setDetay(d => ({ ...d, [runId]: r.data })) })
      .catch(() => { istenen.current[runId] = false })
  }, [markaId, headers])

  useEffect(() => {
    aktifMarka.current = markaId
    istenen.current = {}
    setOzet(null)
    setGecmis([])
    setDetay({})
    setAcik({})
    setSeoSecim(null)
    setTeknikSecim(null)
    if (markaId) ozetGetir()
  }, [markaId, ozetGetir])

  useEffect(() => {
    if (!ozet) return
    Object.values(ozet.kosumlar || {}).forEach(k => {
      if (k && k.durum === 'bitti') detayGetir(k.id)
    })
  }, [ozet, detayGetir])

  useEffect(() => {
    if (!ozet || !(ozet.kosanlar || []).length) return undefined
    const t = setInterval(() => ozetGetir(true), 6000)
    return () => clearInterval(t)
  }, [ozet, ozetGetir])

  function calistir(tur) {
    if (!markaId) return
    const govde = tur === 'geo'
      ? { tur: 'geo', yorum: yorumUret }
      : { tur: 'seo', sablon: sablonDahil, yorum: yorumUret }
    if (tur !== 'geo' && sablonDahil && !window.confirm('Şablon taraması markanın sitesine yaklaşık 36 istek atacak. Devam edilsin mi?')) return
    setBusy(true)
    axios.post(API + '/admin/brands/' + markaId + '/sgo/calistir', govde, { headers })
      .then(() => { flash('Tarama başlatıldı, arka planda koşuyor'); ozetGetir(true) })
      .catch(e => flash((e.response && e.response.data && e.response.data.error) || e.message, false))
      .finally(() => setBusy(false))
  }

  if (!selectedBrand) return <div style={ui.notice}>Önce bir marka seçin.</div>

  const kosumlar = (ozet && ozet.kosumlar) || {}
  const kosanlar = (ozet && ozet.kosanlar) || []
  const esik = (ozet && ozet.esik) || 0.2

  const seoAdaylari = ['seo-aylik', 'seo-gunluk'].map(t => kosumlar[t]).filter(Boolean)
  const seoBitmis = seoAdaylari.filter(k => k.durum === 'bitti')
    .sort((a, b) => new Date(b.basladi) - new Date(a.basladi))
  const seoKosum = (seoSecim && kosumlar[seoSecim]) || seoBitmis[0] || seoAdaylari[0] || null
  const geoKosum = kosumlar.geo || null

  const tumTurler = ['seo-gunluk', 'seo-aylik', 'geo'].filter(t => kosumlar[t])
  const teknikTur = (teknikSecim && kosumlar[teknikSecim] ? teknikSecim : null)
    || (seoKosum ? seoKosum.tur : null) || tumTurler[0] || null
  const teknikKosum = teknikTur ? kosumlar[teknikTur] : null

  function bulgulariAl(kosum) {
    if (!kosum || kosum.durum !== 'bitti') return []
    const d = detay[kosum.id]
    return (d && d.bulgular) || []
  }

  function sadeBolum(kosum, baslik, bosMetin) {
    if (!kosum) {
      return (
        <div style={{ ...s.card, marginBottom: '16px' }}>
          <p style={{ ...s.sectionTitle, marginBottom: '4px' }}>{baslik}</p>
          <p style={ui.emptyText}>{bosMetin}</p>
        </div>
      )
    }
    if (kosum.durum === 'hata') {
      return (
        <div style={{ ...s.card, marginBottom: '16px' }}>
          <p style={{ ...s.sectionTitle, marginBottom: '8px' }}>{baslik}</p>
          <div style={s.alertDanger}>Son tarama hata ile durdu: {kosum.hata || 'sebep kaydedilmedi'}</div>
        </div>
      )
    }
    if (kosum.durum !== 'bitti') {
      return (
        <div style={{ ...s.card, marginBottom: '16px' }}>
          <p style={{ ...s.sectionTitle, marginBottom: '8px' }}>{baslik}</p>
          <div style={s.alertInfo}>Tarama şu an koşuyor, {tarih(kosum.basladi)} itibarıyla başladı.</div>
        </div>
      )
    }

    const bulgular = bulgulariAl(kosum)
    const kusurlar = bulgular.filter(b => b.sinif === 'kusur')
      .sort((a, b) => (AGIRLIK_SIRA[a.agirlik] - AGIRLIK_SIRA[b.agirlik]) || (Number(b.puan_etkisi) - Number(a.puan_etkisi)))
    const ozellikler = bulgular.filter(b => b.sinif === 'ozellik')
      .sort((a, b) => Number(b.oran) - Number(a.oran))
    const oncelikler = (kosum.ozet && kosum.ozet.oncelikler) || []

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{ ...s.flexBetween, flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
          <div style={{ minWidth: 0 }}>
            <p style={s.sectionTitle}>{baslik}</p>
            <p style={s.sectionSubtitle}>
              {TUR_ADI[kosum.tur]} · {tarih(kosum.basladi)} · {bin(kosum.urun_sayisi)} ürün incelendi
            </p>
          </div>
          {seoBitmis.length > 1 && kosum.tur.indexOf('seo') === 0 && (
            <div style={ui.segmented}>
              {seoBitmis.map(k => (
                <button key={k.tur} style={ui.segmentedBtn(kosum.tur === k.tur)} onClick={() => setSeoSecim(k.tur)}>
                  {TUR_KISA[k.tur]}
                </button>
              ))}
            </div>
          )}
        </div>

        {kosum.tur === 'seo-aylik' && (
          <div style={{ ...ui.notice, marginBottom: '16px' }}>
            Aylık tarama sayfa şablonunu da ölçer ve şablon kusurları eşiğe tabi olmadığı için puanı doğrudan düşürür.
            Bu yüzden aylık puan, günlük puanla doğrudan kıyaslanmaz.
          </div>
        )}

        {kosum.tur === 'geo' && kosum.ozet && kosum.ozet.yalnizSiteDuzeyi && (
          <div style={{ ...ui.notice, marginBottom: '16px' }}>
            Bu markada GEO içeriği yayınlanmış ürün yok. Puan yalnızca site düzeyi kontrollerden geliyor,
            yani robots.txt ve llms.txt dosyalarından. Ürün düzeyinde ölçülen bir şey olmadığı için bu puanı,
            GEO içeriği olan markaların puanıyla kıyaslamayın.
          </div>
        )}

        {kosum.yorum_sade && (
          <div style={{ ...s.card, marginBottom: '16px' }}>
            <p style={{ fontSize: '13.5px', color: colors.textPrimary, margin: 0, lineHeight: 1.7 }}>{kosum.yorum_sade}</p>
            <Oncelikler liste={oncelikler} />
          </div>
        )}

        {!kosum.yorum_sade && (
          <div style={{ ...ui.notice, marginBottom: '16px' }}>
            {yorumNotu(kosum)}
          </div>
        )}

        {kusurlar.length > 0 && (
          <>
            <p style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, margin: '0 0 8px' }}>
              Düzeltilecekler ({kusurlar.length})
            </p>
            {kusurlar.map(b => (
              <SadeKusur key={b.id} bulgu={b} acikMi={!!acik[b.id]} ac={() => setAcik(a => ({ ...a, [b.id]: !a[b.id] }))}
                markaId={markaId} api={API} headers={headers} />
            ))}
          </>
        )}

        {!kusurlar.length && detay[kosum.id] && (
          <div style={{ ...s.alertSuccess, marginBottom: '12px' }}>Bu taramada düzeltilecek kusur çıkmadı.</div>
        )}

        {!detay[kosum.id] && <p style={ui.emptyText}>Bulgular yükleniyor…</p>}

        <div style={{ marginTop: kusurlar.length ? '16px' : 0 }}>
          <OzellikKutusu bulgular={ozellikler} esik={esik} />
        </div>
      </div>
    )
  }

  const teknikBulgular = bulgulariAl(teknikKosum)
  const teknikOzet = (teknikKosum && teknikKosum.ozet) || {}

  return (
    <div>
      <div style={{ ...s.flexBetween, marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={s.pageTitle}>Smart SGO</h1>
          <p style={s.pageSubtitle}>
            Katalogun arama motoru ve yapay zeka görünürlüğünü ölçer, kusurları katalogun genel durumundan ayırır
          </p>
        </div>
        {teknikYetki && (
          <div style={ui.segmented}>
            <button style={ui.segmentedBtn(sekme === 'sade')} onClick={() => setSekme('sade')}>Özet</button>
            <button style={ui.segmentedBtn(sekme === 'teknik')} onClick={() => setSekme('teknik')}>Teknik</button>
          </div>
        )}
      </div>

      {msg && <div style={{ ...(msg.ok ? s.alertSuccess : s.alertDanger), marginBottom: '16px' }}>{msg.text}</div>}

      {kosanlar.length > 0 && (
        <div style={{ ...s.alertInfo, marginBottom: '16px' }}>
          {kosanlar.map(k => TUR_ADI[k.tur] || k.tur).join(', ')} şu an koşuyor. Sonuç bitince bu sayfa kendiliğinden yenilenir.
        </div>
      )}

      {yukleniyor && !ozet && <p style={ui.emptyText}>Yükleniyor…</p>}

      {ozet && !tumTurler.length && (
        <div style={s.card}>
          <p style={{ ...s.sectionTitle, marginBottom: '4px' }}>Henüz tarama yapılmadı</p>
          <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0, lineHeight: 1.7 }}>
            Veritabanı kontrolleri ve GEO taraması her gün 04:10'da, sayfa şablonu taraması ayın ilk günü 04:40'ta koşar.
            {yonetimYetki && ' Otomatik tarama yalnız SGO_MARKALAR listesindeki markaları kapsar, aşağıdaki düğmeyle elle başlatabilirsiniz.'}
          </p>
          {yonetimYetki && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
              <button style={s.btnPrimary} onClick={() => calistir('seo')} disabled={busy}>SEO taramasını başlat</button>
              <button style={s.btnSecondary} onClick={() => calistir('geo')} disabled={busy}>GEO taramasını başlat</button>
            </div>
          )}
        </div>
      )}

      {ozet && tumTurler.length > 0 && sekme === 'sade' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobil ? '1fr' : 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ ...ui.statBox, display: 'flex', alignItems: 'center', gap: '16px' }}>
              <PuanHalkasi puan={seoKosum && seoKosum.puan} />
              <div style={{ minWidth: 0 }}>
                <p style={ui.statLabel}>Arama motoru puanı</p>
                <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '6px 0 0', lineHeight: 1.5 }}>
                  {seoKosum ? bin(seoKosum.urun_sayisi) + ' ürün' : 'ölçülmedi'}
                </p>
                <p style={{ fontSize: '11.5px', color: colors.textMuted, margin: '2px 0 0' }}>
                  {seoKosum ? tarih(seoKosum.basladi) : ''}
                </p>
              </div>
            </div>
            <div style={{ ...ui.statBox, display: 'flex', alignItems: 'center', gap: '16px' }}>
              <PuanHalkasi puan={geoKosum && geoKosum.puan} />
              <div style={{ minWidth: 0 }}>
                <p style={ui.statLabel}>Yapay zeka görünürlüğü</p>
                <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '6px 0 0', lineHeight: 1.5 }}>
                  {geoKosum ? bin(geoKosum.urun_sayisi) + ' ürün' : 'içerik yayınlanmadı'}
                </p>
                <p style={{ fontSize: '11.5px', color: colors.textMuted, margin: '2px 0 0' }}>
                  {geoKosum ? tarih(geoKosum.basladi) : ''}
                </p>
              </div>
            </div>
            <Olcu
              etiket="Düzeltilecek madde"
              deger={bin((seoKosum ? seoKosum.kusur_sayisi : 0) + (geoKosum ? geoKosum.kusur_sayisi : 0))}
              alt={'Son SEO ve GEO taramasının toplamı' + (seoKosum && geoKosum && String(seoKosum.basladi).slice(0, 10) !== String(geoKosum.basladi).slice(0, 10) ? ', farklı tarihler' : '')}
            />
            <Olcu
              etiket="Katalogun genel durumu"
              deger={bin((seoKosum ? seoKosum.ozellik_sayisi : 0) + (geoKosum ? geoKosum.ozellik_sayisi : 0))}
              alt={'Kapsamı ' + oranYuzde(esik) + ' üstünde olan madde'}
            />
          </div>

          {sadeBolum(seoKosum, 'Arama motoru', 'Bu marka için SEO taraması henüz koşmadı.')}
          {sadeBolum(geoKosum, 'Yapay zeka görünürlüğü',
            'GEO taraması yalnız bizim içerik eklediğimiz ürünlerin sayfalarını kapsar. Bu markada henüz yayınlanmış içerik yok.')}
        </>
      )}

      {ozet && tumTurler.length > 0 && sekme === 'teknik' && teknikYetki && (
        <>
          <div style={ui.tabBar}>
            {tumTurler.map(t => (
              <button key={t} style={ui.tab(teknikTur === t)} onClick={() => setTeknikSecim(t)}>
                {TUR_KISA[t]}
              </button>
            ))}
          </div>

          {teknikKosum && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: mobil ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <Olcu etiket="Puan" deger={ondalik(teknikKosum.puan, 1)} alt={puanEtiketi(teknikKosum.puan)} />
                <Olcu etiket="İncelenen" deger={bin(teknikKosum.urun_sayisi)} alt="ürün" />
                <Olcu etiket="Kusur" deger={bin(teknikKosum.kusur_sayisi)} alt="puan düşüren madde" />
                <Olcu etiket="Katalog özelliği" deger={bin(teknikKosum.ozellik_sayisi)} alt="puana işlemeyen madde" />
                <Olcu etiket="Süre" deger={sure(teknikKosum.basladi, teknikKosum.bitti) || '-'} alt={'koşum #' + teknikKosum.id} />
              </div>

              <div style={{ ...ui.notice, marginBottom: '16px', lineHeight: 1.7 }}>
                <b>{TUR_ADI[teknikKosum.tur]}</b> · {TUR_KAYNAK[teknikKosum.tur]} · durum {teknikKosum.durum}
                {teknikKosum.hata && <> · hata: {teknikKosum.hata}</>}
                <br />
                Puan 100'den başlar. Ürün düzeyi kusurda düşüş <b>ağırlık × (oran / {oranYuzde(esik)})</b>, tavanı ağırlığın
                kendisidir; oran {oranYuzde(esik)} üstüne çıkarsa madde katalog özelliği sayılır ve puan düşmez.
                Saha kusurunda düşüş <b>ağırlık × oran</b>, örneklem üzerinden ölçüldüğü için eşiğe tabi değildir.
                Ağırlıklar kritik 30, yüksek 18, orta 9, düşük 4.
              </div>

              {teknikKosum.yorum_teknik && (
                <div style={{ ...s.card, marginBottom: '16px' }}>
                  <p style={{ ...s.sectionTitle, marginBottom: '8px' }}>Yapay zeka yorumu</p>
                  <p style={{ fontSize: '13px', color: colors.textPrimary, margin: 0, lineHeight: 1.7 }}>{teknikKosum.yorum_teknik}</p>
                </div>
              )}

              <div style={{ ...s.card, padding: mobil ? '14px' : '20px 24px' }}>
                <p style={{ ...s.sectionTitle, marginBottom: '12px' }}>Bulgular ({teknikBulgular.length})</p>
                {!detay[teknikKosum.id] && <p style={ui.emptyText}>Yükleniyor…</p>}
                {detay[teknikKosum.id] && !teknikBulgular.length && <p style={ui.emptyText}>Bu koşumda bulgu yok.</p>}
                {teknikBulgular.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Kontrol</th>
                          <th style={s.th}>Kategori</th>
                          <th style={s.th}>Ağırlık</th>
                          <th style={s.th}>Sınıf</th>
                          <th style={{ ...s.th, textAlign: 'right' }}>Etkilenen</th>
                          <th style={{ ...s.th, textAlign: 'right' }}>Oran</th>
                          <th style={{ ...s.th, textAlign: 'right' }}>Puan etkisi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teknikBulgular.map(b => (
                          <React.Fragment key={b.id}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => setAcik(a => ({ ...a, [b.id]: !a[b.id] }))}>
                              <td style={s.td}>
                                <div style={{ fontWeight: 600 }}>{b.baslik}</div>
                                <div style={{ fontSize: '11px', color: colors.textMuted, fontFamily: 'monospace' }}>{b.kontrol}</div>
                              </td>
                              <td style={s.td}>
                                <span style={ui.chip}>{KATEGORI_ADI[b.kategori] || b.kategori}</span>
                                {sablonCapinda(b) && <div style={{ marginTop: '4px' }}><span style={ui.chipStrong}>Şablon çapında</span></div>}
                              </td>
                              <td style={s.td}><span style={agirlikKutusu(b.agirlik)}>{AGIRLIK_ADI[b.agirlik] || b.agirlik}</span></td>
                              <td style={s.td}>
                                <span style={b.sinif === 'kusur' ? s.badgeDanger : s.badgeGray}>
                                  {b.sinif === 'kusur' ? 'Kusur' : 'Katalog özelliği'}
                                </span>
                              </td>
                              <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                {bin(b.etkilenen)} / {bin(b.toplam)}
                              </td>
                              <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{oranYuzde(b.oran)}</td>
                              <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                {Number(b.puan_etkisi) > 0 ? '-' + ondalik(b.puan_etkisi, 2) : '0'}
                              </td>
                            </tr>
                            {acik[b.id] && (
                              <tr>
                                <td style={{ ...s.td, background: colors.bgPage }} colSpan={7}>
                                  {b.aciklama && <p style={{ fontSize: '12.5px', color: colors.textPrimary, margin: '0 0 8px', lineHeight: 1.65 }}>{b.aciklama}</p>}
                                  {b.oneri && (
                                    <p style={{ fontSize: '12.5px', color: colors.textSecondary, margin: 0, lineHeight: 1.65 }}>
                                      <b>Öneri:</b> {b.oneri}
                                    </p>
                                  )}
                                  {(b.ornekler || []).length > 0
                                    ? <OrnekListesi ornekler={b.ornekler} sinir={12} />
                                    : <p style={{ ...ui.emptyText, margin: '10px 0 0' }}>Tarama anında örnek kaydedilmedi.</p>}
                                  <TamListe bulgu={b} markaId={markaId} api={API} headers={headers} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <KelimeGrafigi kelime={teknikOzet.kelime} />
              <SahaOzeti ozet={teknikOzet} />
            </>
          )}

          {yonetimYetki && (
            <div style={s.card}>
              <p style={{ ...s.sectionTitle, marginBottom: '4px' }}>Elle tarama</p>
              <p style={{ ...s.sectionSubtitle, marginBottom: '14px' }}>
                Aynı marka için bir tarama koşarken yenisi başlatılamaz
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sablonDahil} onChange={e => setSablonDahil(e.target.checked)} />
                  Şablon taraması dahil, markanın sitesine yaklaşık 36 istek gider
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={yorumUret} onChange={e => setYorumUret(e.target.checked)} />
                  Yapay zeka yorumu üret, koşum başına bir OpenAI çağrısı harcanır
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button style={s.btnPrimary} onClick={() => calistir('seo')} disabled={busy || kosanlar.length > 0}>
                  SEO taraması
                </button>
                <button style={s.btnSecondary} onClick={() => calistir('geo')} disabled={busy || kosanlar.length > 0}>
                  GEO taraması
                </button>
              </div>
            </div>
          )}

          {gecmis.length > 0 && (
            <div style={{ ...s.card, padding: mobil ? '14px' : '20px 24px' }}>
              <p style={{ ...s.sectionTitle, marginBottom: '12px' }}>Koşum geçmişi</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>Tür</th>
                      <th style={s.th}>Durum</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Ürün</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Kusur</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Puan</th>
                      <th style={s.th}>Başladı</th>
                      <th style={s.th}>Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gecmis.map(g => (
                      <tr key={g.id}>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{g.id}</td>
                        <td style={s.td}>{TUR_KISA[g.tur] || g.tur}</td>
                        <td style={s.td}>
                          <span style={g.durum === 'bitti' ? s.badgeSuccess : (g.durum === 'hata' ? s.badgeDanger : s.badgeInfo)}>
                            {g.durum}
                          </span>
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{bin(g.urun_sayisi)}</td>
                        <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{bin(g.kusur_sayisi)}</td>
                        <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ondalik(g.puan, 1)}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{tarih(g.basladi)}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{sure(g.basladi, g.bitti) || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
